import { useEffect, useState } from 'react'
import { useLiveQuery } from 'dexie-react-hooks'
import StatCard from '@/components/StatCard'
import SyncBar from '@/components/SyncBar'
import Sparkline from '@/components/charts/Sparkline'
import YearAgoCard from '@/components/YearAgoCard'
import RecordsStrip from '@/components/RecordsStrip'
import ShareMonthButton from '@/components/ShareMonthButton'
import { db } from '@/db/schema'
import { dailySeries, totalCounts, type DailyPoint } from '@/db/queries'

export default function Dashboard() {
  const counts = useLiveQuery(() => totalCounts(), [], null)
  const cursor = useLiveQuery(() => db.cursors.get('cycle'), [])
  const [series, setSeries] = useState<DailyPoint[]>([])

  useEffect(() => {
    dailySeries(30).then(setSeries)
  }, [counts])

  const today = series[series.length - 1]
  const yesterday = series[series.length - 2]
  const isFirstSync = !counts || (counts.cycles === 0 && counts.recoveries === 0)
  const lastSync = cursor?.last_run_iso

  return (
    <div className="space-y-8">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Today</h1>
          <p className="text-sm text-stone-500 mt-1">
            {lastSync
              ? `Last synced ${new Date(lastSync).toLocaleString()}`
              : 'No data yet — run a sync to pull your history.'}
          </p>
        </div>
        <div className="flex-shrink-0">
          <SyncBar autoStart={isFirstSync} />
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard
          label="Recovery"
          value={today?.recovery != null ? Math.round(today.recovery) : null}
          unit="%"
          tone="recovery"
          score={today?.recovery ?? null}
          hint={delta(today?.recovery, yesterday?.recovery, '%')}
        />
        <StatCard
          label="Strain"
          value={today?.strain != null ? today.strain.toFixed(1) : null}
          tone="strain"
          hint={delta(today?.strain, yesterday?.strain)}
        />
        <StatCard
          label="HRV"
          value={today?.hrv != null ? Math.round(today.hrv) : null}
          unit="ms"
          hint={delta(today?.hrv, yesterday?.hrv, ' ms')}
        />
        <StatCard
          label="Sleep"
          value={today?.sleep_minutes != null ? formatMinutes(today.sleep_minutes) : null}
          tone="sleep"
          hint={
            today?.sleep_performance != null
              ? `${Math.round(today.sleep_performance)}% performance`
              : undefined
          }
        />
      </div>

      <YearAgoCard />

      <RecordsStrip />

      <section>
        <div className="flex items-baseline justify-between mb-3 gap-3 flex-wrap">
          <h2 className="text-sm uppercase tracking-wider text-stone-500">Last 30 days</h2>
          <ShareMonthButton />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <SparklineCard
            label="Recovery"
            color="#16a34a"
            domain={[0, 100]}
            data={series.map((s) => ({ day: s.day, value: s.recovery ?? null }))}
          />
          <SparklineCard
            label="Strain"
            color="#0ea5e9"
            domain={[0, 21]}
            data={series.map((s) => ({ day: s.day, value: s.strain ?? null }))}
          />
          <SparklineCard
            label="HRV"
            color="#a855f7"
            data={series.map((s) => ({ day: s.day, value: s.hrv ?? null }))}
          />
        </div>
      </section>
    </div>
  )
}

function SparklineCard({
  label,
  color,
  domain,
  data,
}: {
  label: string
  color: string
  domain?: [number, number]
  data: { day: string; value: number | null }[]
}) {
  return (
    <div className="rounded-xl border border-stone-200 bg-white p-4 shadow-sm">
      <div className="text-xs uppercase tracking-wider text-stone-500 mb-2">{label}</div>
      <Sparkline data={data} color={color} domain={domain} />
    </div>
  )
}

function delta(a?: number | null, b?: number | null, unit = ''): string | undefined {
  if (a == null || b == null) return undefined
  const d = a - b
  const sign = d > 0 ? '+' : ''
  return `${sign}${d.toFixed(d < 1 && d > -1 ? 1 : 0)}${unit} vs yesterday`
}

function formatMinutes(m: number): string {
  const h = Math.floor(m / 60)
  const min = m % 60
  return `${h}h ${min}m`
}
